import PasswordValidator from "password-validator";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

import { useStore } from "@state/store";
import { useCheckScreenWidth } from "@util/hooks";
import { MailingAddress } from "shopify-storefront-api-typings";
import { StoreNewCartShopify } from "./types";

export type StickyKeys = "iban";

export const isBrowser = () => typeof window !== "undefined";

export const mergeClassNames = (
  ...classes: (string | boolean | undefined | null)[]
) => {
  return classes.filter(Boolean).join(" ");
};

export function usePrevious<T>(value: T) {
  const ref = useRef<T>(value);

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
}

export function useStickyState<S>(
  defaultValue: S | (() => S),
  key: StickyKeys
): [S, Dispatch<SetStateAction<S>>] {
  const [value, setValue] = useState(() => {
    const stickyValue = isBrowser() && window.localStorage.getItem(key);

    return stickyValue != null && stickyValue !== false
      ? JSON.parse(stickyValue)
      : defaultValue;
  });

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(value));
  }, [key, value]);
  return [value, setValue];
}

export function setStickyState(key: StickyKeys, value: any) {
  return isBrowser() && window.localStorage.setItem(key, JSON.stringify(value));
}

export function getStickyState(key: StickyKeys) {
  const stickyValue = isBrowser() && window.localStorage.getItem(key);
  return stickyValue != null && stickyValue !== false
    ? JSON.parse(stickyValue)
    : undefined;
}

export function arraysEquality(array1: any[], array2: any[]) {
  return (
    array1.length === array2.length &&
    array1.every((value, index) => value === array2[index])
  );
}

export function objectEquality(object1: any, object2: any) {
  return Object.keys(object1).every(key => object1[key] === object2[key]);
}

export function validatePassword() {
  const PasswordSchema = new PasswordValidator();
  return PasswordSchema.is()
    .min(8)
    .is()
    .max(100)
    .has()
    .lowercase()
    .has()
    .uppercase();
}

export function clearCategoryState() {
  useStore.setState({
    categoryParams: {
      categoryHandle: "",
      categoryName: "",
      hasCategory: false,
    },
  });
}

export function pageWidthMargin() {
  const { isLaptopWidth, isSmallLaptopWidth, isTabletWidth, isMobileWidth } =
    useCheckScreenWidth();
  if (isMobileWidth) {
    return "27px";
  }
  if (isTabletWidth) {
    return "35px";
  }
  if (isSmallLaptopWidth) {
    return "70px";
  }
  if (isLaptopWidth) {
    return "100px";
  }
  return "130px";
}

export const setSavedRecipes = (savedIds: string[]) => {
  useStore.setState({ savedRecipes: savedIds });
};

export const setSavedProducts = (savedIds: string[]) => {
  useStore.setState({ savedProducts: savedIds });
};

export const setSavedBlogs = (savedIds: string[]) => {
  useStore.setState({ savedBlogs: savedIds });
};

export const maximumWidth = () => {
  const { isTabletWidth, isMobileWidth } = useCheckScreenWidth();
  if (isTabletWidth) {
    return "calc(100% - 110px)";
  }
  if (isMobileWidth) {
    return "calc(100% - 60px)";
  }
  return "945px";
};

export const moneyFormat = (money: bigint | number) => {
  const formatter = new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  });
  if (money === undefined || money === null) return;
  return formatter.format(money);
};

export const dateFormat = (date: string) => {
  const formatter = new Intl.DateTimeFormat("en-NZ", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
  if (date === undefined || date === null) return;
  return formatter.format(new Date(date));
};

export const addressToString = (address?: MailingAddress | null) => {
  if (!address) return undefined;

  const addressArr = [
    ...new Set(
      [
        address.company,
        address.address1,
        address.address2,
        address.city,
        address.province,
        address.zip,
        address.country,
      ].filter(line => {
        if (!line) return false;
        return true;
      })
    ),
  ];

  return addressArr.join(", ");
};

export const blockToText = (blocks: any) => {
  return blocks.map((block: any) =>
    block.children.map((child: any) => child.text).join("")
  );
};

export function useDebounce<T>(value: T, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

export const removeTags = (str: string) => {
  if (str === null || str === "") return false;
  else str = str.toString();

  // Regular expression to identify HTML tags in
  // the input string. Replacing the identified
  // HTML tag with a null string.
  var string = str.replace(/(<([^>]+)>)/gi, "");
  string.replace("&#039;", "'");
  return string;
};

export const toPlainText = (
  blocks: Queries.Maybe<Queries.SanityBlockContent> | undefined
) => {
  if (!blocks?._rawBlocks) return null;
  return (
    (blocks?._rawBlocks as any)
      ?.map((block: any) => {
        // if it's not a text block with children,
        // return nothing
        if (block._type !== "block" || !block.children) {
          return "";
        }
        // loop through the children spans, and join the
        // text strings
        return block.children.map((child: any) => child.text).join("");
      })
      // join the paragraphs leaving split by two linebreaks
      ?.join("\n\n")
  );
};

//Bundles
export const getBundle = (bundleId: Queries.Maybe<string> | undefined) => {
  const { bundles } = useStore();

  const bundle = bundles && bundles.find(bundle => bundle.id === bundleId);
  return bundle;
};

export interface UpdateCartAttr {
  newCartShopify: StoreNewCartShopify;
  keyName: string;
  newValue: string;
}

/**
 * Updates a cart attribute inside the `newCartShopify` object if one exist,
 * otherwise it will create a new one.
 * @return
 */
export function updateCartAttr({
  newCartShopify,
  keyName,
  newValue,
}: UpdateCartAttr) {
  if (!newCartShopify.attributes) return newCartShopify;

  const cartAttrIndex = newCartShopify.attributes.findIndex(
    attr => attr.key === keyName
  );

  if (cartAttrIndex !== -1) {
    if (newValue === "") {
      newCartShopify.attributes.splice(cartAttrIndex, 1);
    } else {
      newCartShopify.attributes[cartAttrIndex].value = newValue;
    }
  } else if (newValue !== "") {
    newCartShopify.attributes.push({ key: keyName, value: newValue });
  }

  return newCartShopify;
}

export const fixTimeMinLabel = (time?: any, extraLabel?: string) => {
  if (!time) return "";
  if (String(time).includes("min")) {
    return time + (extraLabel ? " " + extraLabel : "");
  }
  return time + " min" + (extraLabel ? " " + extraLabel : "");
};
